import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Zap, Clock, Plus, Trash2, ToggleLeft, ToggleRight, AlertTriangle,
  RefreshCw, X, Search, Package,
} from 'lucide-react';
import flashDealService from '../../services/flashDealService';
import productService from '../../services/productService';

const fmt = (price) => `₹${Number(price).toFixed(2)}`;

const hoursColor = (h) => {
  if (h <= 12) return 'text-red-600 bg-red-50';
  if (h <= 24) return 'text-orange-600 bg-orange-50';
  return 'text-yellow-700 bg-yellow-50';
};

/* ── Shared discount form fields ────────────────────────────── */
function DiscountFields({ form, onChange, originalPrice }) {
  const isFlat = form.discountType === 'flat';

  const discountedPrice = isFlat
    ? Math.max(0, originalPrice - Number(form.discountAmount || 0))
    : originalPrice * (1 - Number(form.discountPercent || 0) / 100);

  return (
    <div className="space-y-2">
      {/* Discount type toggle */}
      <fieldset className="border-0 p-0 m-0">
        <legend className="text-xs text-gray-500 mb-1">Discount Type</legend>
        <div className="flex rounded-lg border border-gray-300 overflow-hidden text-sm font-medium">
          <button
            type="button"
            onClick={() => onChange({ discountType: 'percent' })}
            className={`flex-1 py-1.5 transition-colors ${
              isFlat ? 'bg-white text-gray-600 hover:bg-gray-50' : 'bg-[#548B2E] text-white'
            }`}
          >
            % Percentage
          </button>
          <button
            type="button"
            onClick={() => onChange({ discountType: 'flat' })}
            className={`flex-1 py-1.5 transition-colors ${
              isFlat ? 'bg-[#548B2E] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            ₹ Flat Amount
          </button>
        </div>
      </fieldset>

      {/* Value input */}
      <div>
        <label htmlFor="discount-value" className="text-xs text-gray-500 block mb-1">
          {isFlat ? 'Discount Amount (₹)' : 'Discount (%)'}
        </label>
        {isFlat ? (
          <input
            id="discount-value"
            type="number" min={0.5} step={0.5}
            value={form.discountAmount}
            onChange={(e) => onChange({ discountAmount: e.target.value })}
            placeholder="e.g. 20"
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
          />
        ) : (
          <input
            id="discount-value"
            type="number" min={1} max={90}
            value={form.discountPercent}
            onChange={(e) => onChange({ discountPercent: e.target.value })}
            className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
          />
        )}
      </div>

      {/* Flash price preview */}
      {originalPrice > 0 && (
        <p className="text-xs font-semibold text-green-700">
          Flash price: {fmt(Math.max(0, discountedPrice))}
          <span className="text-gray-400 font-normal ml-1">(was {fmt(originalPrice)})</span>
        </p>
      )}
    </div>
  );
}

DiscountFields.propTypes = {
  form: PropTypes.shape({
    discountType: PropTypes.string.isRequired,
    discountPercent: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    discountAmount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  originalPrice: PropTypes.number.isRequired,
};

/* ── Deal creation payload builder ─────────────────────────── */
function buildCreatePayload(productId, form) {
  const now = new Date();
  const ends = new Date(now.getTime() + form.durationHours * 3600 * 1000);
  const base = {
    productId: Number(productId),
    discountType: form.discountType,
    startsAt: now.toISOString(),
    endsAt: ends.toISOString(),
    maxQuantity: form.maxQuantity ? Number(form.maxQuantity) : undefined,
  };
  if (form.discountType === 'flat') {
    base.discountAmount = Number(form.discountAmount);
  } else {
    base.discountPercent = Number(form.discountPercent);
  }
  return base;
}

const DEFAULT_FORM = { discountType: 'percent', discountPercent: 20, discountAmount: '', durationHours: 6, maxQuantity: '' };

/* ── Product search combobox ─────────────────────────────────── */
function ProductSearch({ onSelect }) {

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!query.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await productService.getAll({ search: query });
        setResults(Array.isArray(res) ? res : (res.products ?? res.data ?? []));
      } catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
  }, [query]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (containerRef.current && !containerRef.current.contains(e.target)) setResults([]); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={containerRef}>
      <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-[#548B2E]">
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search product by name…"
          className="flex-1 text-sm outline-none bg-transparent"
        />
        {searching && <RefreshCw className="w-3.5 h-3.5 text-gray-400 animate-spin" />}
      </div>
      {results.length > 0 && (
        <div className="absolute top-full mt-1 left-0 right-0 z-30 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onSelect(p); setQuery(''); setResults([]); }}
              className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 text-left transition-colors"
            >
              {p.images?.[0]?.imageUrl ? (
                <img src={p.images[0].imageUrl} alt={p.name} className="w-8 h-8 rounded object-cover flex-shrink-0" />
              ) : (
                <Package className="w-8 h-8 text-gray-300 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800 truncate">{p.name}</p>
                <p className="text-xs text-gray-500">{fmt(p.pricePerUnit ?? p.price ?? 0)} · Stock: {p.stockQuantity ?? '—'}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
ProductSearch.propTypes = { onSelect: PropTypes.func.isRequired };

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
export default function FlashDealsManagement() {
  const [deals, setDeals] = useState([]);
  const [nearExpiry, setNearExpiry] = useState([]);
  const [threshold, setThreshold] = useState(48);
  const [loading, setLoading] = useState(true);

  // Near-expiry deal form
  const [creating, setCreating] = useState(null);
  const [form, setForm] = useState(DEFAULT_FORM);

  // Normal product deal form
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [normalForm, setNormalForm] = useState(DEFAULT_FORM);
  const [creatingNormal, setCreatingNormal] = useState(false);

  const [message, setMessage] = useState(null);

  // Expired deal notifications
  const [expiredDeals, setExpiredDeals] = useState([]);
  const [restartDeal, setRestartDeal] = useState(null);
  const [restartForm, setRestartForm] = useState(DEFAULT_FORM);
  const [restarting, setRestarting] = useState(false);
  const prevLiveIdsRef = useRef(new Set());
  const initializedRef = useRef(false);

  const showMsg = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allDeals, expiring] = await Promise.all([
        flashDealService.getAll(),
        flashDealService.getNearExpiry(threshold),
      ]);
      setDeals(allDeals);
      setNearExpiry(expiring);
    } catch {
      showMsg('error', 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const interval = setInterval(() => load(), 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Detect live → expired transitions
  useEffect(() => {
    if (loading) return;
    const currentLiveIds = new Set(deals.filter(isLive).map(d => d.id));
    if (!initializedRef.current) {
      prevLiveIdsRef.current = currentLiveIds;
      initializedRef.current = true;
      return;
    }
    const justExpired = deals.filter(d => prevLiveIdsRef.current.has(d.id) && !isLive(d));
    if (justExpired.length > 0) {
      setExpiredDeals(prev => {
        const seen = new Set(prev.map(d => d.id));
        return [...prev, ...justExpired.filter(d => !seen.has(d.id))];
      });
    }
    prevLiveIdsRef.current = currentLiveIds;
  }, [deals, loading]);

  const dismissExpired = (id) => {
    setExpiredDeals(prev => prev.filter(d => d.id !== id));
    if (restartDeal?.id === id) setRestartDeal(null);
  };

  const openRestart = (deal) => {
    setRestartDeal(deal);
    setRestartForm({
      ...DEFAULT_FORM,
      discountType: deal.discountType ?? 'percent',
      discountPercent: deal.discountPercent,
      discountAmount: deal.discountAmount ?? '',
    });
  };

  const submitRestart = async () => {
    if (!restartDeal) return;
    setRestarting(true);
    try {
      await flashDealService.create(buildCreatePayload(restartDeal.productId, restartForm));
      dismissExpired(restartDeal.id);
      showMsg('success', `Flash deal restarted for ${restartDeal.product?.name ?? 'product'}!`);
      load();
    } catch {
      showMsg('error', 'Failed to restart flash deal');
    } finally {
      setRestarting(false);
    }
  };

  // Near-expiry deal submission
  const submitNearExpiryDeal = async () => {
    try {
      await flashDealService.create({
        ...buildCreatePayload(creating.productId, form),
        thresholdHours: threshold,
      });
      setCreating(null);
      showMsg('success', 'Flash deal created!');
      load();
    } catch {
      showMsg('error', 'Failed to create flash deal');
    }
  };

  // Normal product deal submission
  const submitNormalDeal = async () => {
    if (!selectedProduct) return;
    setCreatingNormal(true);
    try {
      await flashDealService.create(buildCreatePayload(selectedProduct.id, normalForm));
      setSelectedProduct(null);
      setNormalForm(DEFAULT_FORM);
      showMsg('success', `Flash deal created for ${selectedProduct.name}!`);
      load();
    } catch {
      showMsg('error', 'Failed to create flash deal');
    } finally {
      setCreatingNormal(false);
    }
  };

  const toggleDeal = async (deal) => {
    try {
      await flashDealService.update(deal.id, { isActive: !deal.isActive });
      load();
    } catch {
      showMsg('error', 'Failed to update deal');
    }
  };

  const deleteDeal = async (id) => {
    if (!globalThis.confirm('Delete this flash deal?')) return;
    try {
      await flashDealService.remove(id);
      load();
    } catch {
      showMsg('error', 'Failed to delete deal');
    }
  };

  const isLive = (deal) => {
    const now = new Date();
    return deal.isActive && new Date(deal.startsAt) <= now && new Date(deal.endsAt) > now;
  };

  const discountLabel = (deal) =>
    deal.discountType === 'flat'
      ? `₹${Number(deal.discountAmount).toFixed(2)} off`
      : `${deal.discountPercent}% off`;

  if (loading) return <div className="p-8 text-center text-gray-500">Loading flash deals…</div>;

  return (
    <div className="space-y-8 p-6">

      {/* ── Expired deal popup notifications ──────────────────── */}
      {expiredDeals.map(deal => (
        <div key={deal.id} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
            <div className="bg-red-500 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <Zap className="w-5 h-5" />
                <h3 className="font-bold text-lg">Flash Deal Ended</h3>
              </div>
              <button onClick={() => dismissExpired(deal.id)} className="text-white/80 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5">
              <p className="text-gray-800 font-semibold text-base mb-1">
                {deal.product?.name ?? `Product #${deal.productId}`}
              </p>
              <p className="text-sm text-gray-500 mb-4">
                {discountLabel(deal)} · Flash price {fmt(deal.discountedPrice)} · Ended {new Date(deal.endsAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </p>

              {restartDeal?.id === deal.id ? (
                <div className="space-y-3 border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <p className="text-sm font-medium text-gray-700">Restart this flash deal</p>
                  <DiscountFields
                    form={restartForm}
                    onChange={(patch) => setRestartForm(f => ({ ...f, ...patch }))}
                    originalPrice={Number(deal.product?.pricePerUnit ?? deal.discountedPrice)}
                  />
                  <div>
                    <label htmlFor="restart-duration" className="text-xs text-gray-500 block mb-1">Duration (hours)</label>
                    <input
                      id="restart-duration"
                      type="number" min={1} max={48}
                      value={restartForm.durationHours}
                      onChange={e => setRestartForm(f => ({ ...f, durationHours: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                    />
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={submitRestart}
                      disabled={restarting}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-green-700 hover:bg-green-800 disabled:opacity-60 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                    >
                      <RefreshCw className={`w-4 h-4 ${restarting ? 'animate-spin' : ''}`} />
                      {restarting ? 'Launching…' : 'Launch New Deal'}
                    </button>
                    <button onClick={() => setRestartDeal(null)} className="flex-1 border border-gray-300 text-gray-600 text-sm py-2 rounded-lg hover:bg-gray-100 transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    onClick={() => openRestart(deal)}
                    className="flex-1 flex items-center justify-center gap-1.5 bg-green-700 hover:bg-green-800 text-white text-sm font-medium py-2.5 rounded-xl transition-colors"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Restart Flash Deal
                  </button>
                  <button
                    onClick={() => dismissExpired(deal.id)}
                    className="flex-1 border border-gray-300 text-gray-600 text-sm py-2.5 rounded-xl hover:bg-gray-50 transition-colors"
                  >
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* ── Toast message ──────────────────────────────────────── */}
      {message && (
        <div className={`p-3 rounded-lg text-sm ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
          {message.text}
          <button className="ml-4 underline" onClick={() => setMessage(null)}>Dismiss</button>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          SECTION 1: Create flash deal for any product
      ══════════════════════════════════════════════════════════ */}
      <div className="bg-white border border-[#548B2E]/30 rounded-2xl p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Package className="w-5 h-5 text-[#548B2E]" />
          Create Flash Deal for Any Product
        </h2>

        <ProductSearch onSelect={(p) => { setSelectedProduct(p); setNormalForm(DEFAULT_FORM); }} />

        {selectedProduct && (
          <div className="mt-4 border border-gray-200 rounded-xl p-4 bg-gray-50 space-y-3">
            {/* Selected product info */}
            <div className="flex items-center gap-3 pb-2 border-b border-gray-200">
              {selectedProduct.images?.[0]?.imageUrl ? (
                <img src={selectedProduct.images[0].imageUrl} alt={selectedProduct.name} className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <Package className="w-10 h-10 text-gray-300" />
              )}
              <div>
                <p className="font-semibold text-gray-800 text-sm">{selectedProduct.name}</p>
                <p className="text-xs text-gray-500">Price: {fmt(selectedProduct.pricePerUnit ?? selectedProduct.price ?? 0)} · Stock: {selectedProduct.stockQuantity ?? '—'}</p>
              </div>
              <button onClick={() => setSelectedProduct(null)} className="ml-auto text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <DiscountFields
              form={normalForm}
              onChange={(patch) => setNormalForm(f => ({ ...f, ...patch }))}
              originalPrice={Number(selectedProduct.pricePerUnit ?? selectedProduct.price ?? 0)}
            />

            <div className="flex gap-3">
              <div className="flex-1">
                <label htmlFor="normal-duration" className="text-xs text-gray-500 block mb-1">Duration (hours)</label>
                <input
                  id="normal-duration"
                  type="number" min={1} max={720}
                  value={normalForm.durationHours}
                  onChange={(e) => setNormalForm(f => ({ ...f, durationHours: e.target.value }))}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                />
              </div>
              <div className="flex-1">
                <label htmlFor="normal-maxqty" className="text-xs text-gray-500 block mb-1">Max Qty (optional)</label>
                <input
                  id="normal-maxqty"
                  type="number" min={1}
                  value={normalForm.maxQuantity}
                  onChange={(e) => setNormalForm(f => ({ ...f, maxQuantity: e.target.value }))}
                  placeholder="Unlimited"
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#548B2E]"
                />
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <button
                onClick={submitNormalDeal}
                disabled={creatingNormal}
                className="flex-1 flex items-center justify-center gap-2 bg-[#548B2E] hover:bg-[#3a6020] disabled:opacity-60 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
              >
                <Zap className="w-4 h-4" />
                {creatingNormal ? 'Launching…' : 'Launch Flash Deal'}
              </button>
              <button
                onClick={() => setSelectedProduct(null)}
                className="flex-1 border border-gray-300 text-gray-600 text-sm py-2 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          SECTION 2: Near-expiry product suggestions
      ══════════════════════════════════════════════════════════ */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-orange-500" />
            Near-Expiry Products
          </h2>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-600">Show expiring within</span>
            <select
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value))}
              className="border border-gray-300 rounded px-2 py-1 text-sm"
            >
              <option value={24}>24 hours</option>
              <option value={48}>48 hours</option>
            </select>
          </div>
        </div>

        {nearExpiry.length === 0 ? (
          <p className="text-gray-500 text-sm bg-gray-50 rounded-lg p-4">
            No products expiring within {threshold} hours.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {nearExpiry.map((p) => (
              <div key={p.productId} className="bg-white border border-orange-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-start gap-3 mb-3">
                  {p.image && (
                    <img src={p.image} alt={p.productName} className="w-12 h-12 object-cover rounded-lg" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 truncate">{p.productName}</p>
                    <p className="text-sm text-gray-500">Price: {fmt(p.pricePerUnit)}</p>
                    <p className="text-sm text-gray-500">Stock: {p.stockQuantity} units</p>
                  </div>
                </div>
                <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold mb-3 ${hoursColor(p.hoursLeft)}`}>
                  <Clock className="w-3 h-3" />
                  {p.hoursLeft}h left
                </div>

                {creating?.productId === p.productId ? (
                  <div className="space-y-2 border-t pt-3">
                    <DiscountFields
                      form={form}
                      onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
                      originalPrice={p.pricePerUnit}
                    />
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-0.5">Duration (hours)</p>
                        <input
                          aria-label="Duration in hours"
                          type="number" min={1} max={48}
                          value={form.durationHours}
                          onChange={(e) => setForm(f => ({ ...f, durationHours: e.target.value }))}
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs text-gray-500 mb-0.5">Max Qty (optional)</p>
                        <input
                          aria-label="Max quantity"
                          type="number" min={1}
                          value={form.maxQuantity}
                          onChange={(e) => setForm(f => ({ ...f, maxQuantity: e.target.value }))}
                          placeholder="Unlimited"
                          className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={submitNearExpiryDeal} className="flex-1 bg-green-700 text-white text-sm py-1.5 rounded-lg hover:bg-green-800 font-medium">
                        Launch Deal
                      </button>
                      <button onClick={() => setCreating(null)} className="flex-1 border border-gray-300 text-gray-600 text-sm py-1.5 rounded-lg hover:bg-gray-50">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => { setCreating(p); setForm(DEFAULT_FORM); }}
                    className="w-full flex items-center justify-center gap-1 bg-orange-500 hover:bg-orange-600 text-white text-sm py-1.5 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" /> Create Flash Deal
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════
          SECTION 3: All flash deals table
      ══════════════════════════════════════════════════════════ */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Zap className="w-5 h-5 text-yellow-500" />
          All Flash Deals
        </h2>

        {deals.length === 0 ? (
          <p className="text-gray-500 text-sm bg-gray-50 rounded-lg p-4">No flash deals created yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-gray-50 text-left text-gray-600 text-xs uppercase tracking-wide">
                  <th className="px-4 py-3 rounded-tl-lg">Product</th>
                  <th className="px-4 py-3">Discount</th>
                  <th className="px-4 py-3">Flash Price</th>
                  <th className="px-4 py-3">Window</th>
                  <th className="px-4 py-3">Sold</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 rounded-tr-lg">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {deals.map((deal) => (
                  <tr key={deal.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <div className="flex items-center gap-2">
                        {deal.product?.name ?? `Product #${deal.productId}`}
                        {deal.isAutoGenerated && (
                          <span className="px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded text-xs font-medium">Auto</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`font-semibold ${deal.discountType === 'flat' ? 'text-blue-600' : 'text-orange-600'}`}>
                        {discountLabel(deal)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-green-700 font-semibold">{fmt(deal.discountedPrice)}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      <div>{new Date(deal.startsAt).toLocaleString()}</div>
                      <div>→ {new Date(deal.endsAt).toLocaleString()}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {/* quantitySold is fractional for sub-base variant sales — trim to ≤2dp */}
                      {Number(Number(deal.quantitySold).toFixed(2))}{deal.maxQuantity ? ` / ${deal.maxQuantity}` : ''}
                    </td>
                    <td className="px-4 py-3">
                      {(() => {
                        if (isLive(deal)) return (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" /> Live
                          </span>
                        );
                        if (deal.isActive) return (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Scheduled</span>
                        );
                        return (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">Inactive</span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button onClick={() => toggleDeal(deal)} title={deal.isActive ? 'Deactivate' : 'Activate'}>
                          {deal.isActive
                            ? <ToggleRight className="w-5 h-5 text-green-600" />
                            : <ToggleLeft className="w-5 h-5 text-gray-400" />}
                        </button>
                        <button onClick={() => deleteDeal(deal.id)} title="Delete">
                          <Trash2 className="w-4 h-4 text-red-500 hover:text-red-700" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
